import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/shadcn/utils';
import { GoogleAuthProvider } from '@/modules/auth/components/GoogleAuthProvider';
import { enterStep } from '@/modules/careers/careers';

import { SignUpPitch } from './SignUpPitch';

interface SignUpLayoutProps {
  /** The sign-up buttons, error, and notices under the offer. */
  children: ReactNode;
  /**
   * A trial offer link hides the Free plan offer box: the notice under the
   * buttons describes the trial, and the two describe different plans.
   */
  hasTrialOffer?: boolean;
}

/**
 * Chrome for `/sign-up`: the pitch panel on wide screens and the sign-up
 * card beside it. Below `lg` the panel is gone, so the card leads with the
 * pitch heading and the free offer, and nothing follows the form. `/login`
 * keeps `AuthLayout`. Wraps the Google OAuth provider the SSO button needs.
 */
export const SignUpLayout = ({
  children,
  hasTrialOffer = false,
}: SignUpLayoutProps) => (
  <GoogleAuthProvider>
    <div className="portal text-foreground flex min-h-svh flex-col bg-white">
      {/* Spans the whole window, so on large screens the panel and the card's
          half grow together instead of floating in white margins. */}
      <div className="flex w-full flex-1 flex-col gap-6 px-4 pt-6 pb-4 sm:px-8 lg:gap-5 lg:px-14 lg:pb-8">
        {/* Phones centre the logo and move Log in below the form. */}
        <header className="flex items-center justify-center gap-4 lg:justify-between">
          <a
            href="https://finlay.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="focus-visible:ring-ring/50 rounded-sm outline-none focus-visible:ring-[3px]"
          >
            <img
              src="/logo.png"
              alt="Finlay"
              className="h-[35px] w-auto object-contain"
            />
          </a>
          <LogInPrompt className="hidden text-right lg:block" />
        </header>

        {/* Rises in once, in reading order (`[data-enter-step]` in index.css). */}
        <div
          className="flex flex-1 gap-6 lg:grid lg:grid-cols-[minmax(0,1.12fr)_minmax(0,1fr)] lg:gap-10"
          data-enter
        >
          <div className="hidden lg:flex lg:flex-col">
            <SignUpPitch />
          </div>

          <div className="flex flex-1 flex-col items-center justify-start sm:justify-center">
            {/* One 400px column, the width of Google's button, so the heading
                lines up with the buttons below it. */}
            <main className="sm:border-primary/20 flex w-full max-w-[496px] flex-col gap-8 rounded-[20px] bg-white sm:border sm:px-12 sm:py-16 sm:shadow-[0_12px_40px_rgba(107,78,255,0.06)]">
              <div className="flex flex-col gap-5">
                {/* Without the pitch panel beside it, the card pitches too:
                    Slack is what sets Finlay apart, so phones read where it
                    works. Beside the panel, which sells Finlay, the card leads
                    with the offer instead. */}
                <h1
                  {...enterStep(3)}
                  className="text-foreground text-[32px] leading-[1.15] font-semibold tracking-[-0.0225em] sm:text-[40px] lg:text-[32px] lg:tracking-[-0.02em]"
                >
                  <span className="lg:hidden">
                    Describe the role. <br />
                    Finlay runs the rest <br />
                    in Slack.
                  </span>
                  <span className="hidden lg:inline">
                    Start your first role free
                  </span>
                </h1>
                {/* Paid ads land here on phones, so the free offer sits above
                    the buttons where it can't be missed. It also says what
                    Finlay does, so there's no subtitle repeating it. */}
                {!hasTrialOffer && <FreeOffer />}
              </div>
              <div {...enterStep(5)} className="flex flex-col gap-6">
                {children}
                <LogInPrompt className="text-center lg:hidden" />
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  </GoogleAuthProvider>
);

/**
 * What the Free plan includes for the first role, in the visitor's words:
 * "contacted" is Free's automated outreach and "AI screening calls" its
 * pre-screen calls (see the help centre's Plans and limits). Phones title it,
 * since their heading doesn't say "free".
 */
const FreeOffer = () => (
  <p
    {...enterStep(4)}
    className="bg-secondary-150 text-muted-foreground flex flex-col gap-0.5 rounded-xl px-4 py-3 text-sm leading-normal"
  >
    <span className="text-primary font-semibold lg:hidden">
      Your first role is free
    </span>
    50 candidates found and contacted, plus 3 AI screening calls.
  </p>
);

/** Kept at the top on wide screens and below the form on phones. */
const LogInPrompt = ({ className }: { className: string }) => (
  <p className={cn('text-muted-foreground text-sm', className)}>
    Already have an account?{' '}
    <Link
      to="/login"
      // The padding makes a 44px tap target without moving the text.
      className="text-primary hover:text-primary-700 focus-visible:ring-ring/50 -my-3 inline-block rounded-sm py-3 font-semibold underline-offset-4 outline-none hover:underline focus-visible:ring-[3px]"
    >
      Log in
    </Link>
  </p>
);
